<script lang="ts">
    import cn from 'classnames';
    import { configService } from '$lib/services';

    import sc from '@components/common/common.module.css';
    import { Logo } from '@components/layout/header/components/logo';
    import { Menu } from '@components/layout/menu';
    import { SALE_MINUTES_PERCENT, SALE_SUBSCRIPTION_PERCENT } from '@components/payment/constants';
    import { Sale, SalePropType } from '@components/payment/sale';
    import { isMinutesRunningOut, paymentStore, totalMinutes, totalSeconds } from '@components/payment/store';
    import { dialogStore } from '@components/ui/dialog';
    import { isOperator, userStore } from '@components/user/store';
    import { LL } from '@i18n/i18n-svelte';
    import { landingPages, URLS } from '@shared/routing';
    import { Feature, IdentityStatus, PaymentSource, UserStatus } from '@shared/types';
    import { Button, ButtonTypes } from '@uikit/button';
    import { Size } from '@uikit/types';
    import { isNumber } from '@utils/type/checker';

    import { Balance } from './components/balance';
    import { SettingsDropdown } from './components/settings-dropdown';
    import { UnauthButtons } from './components/unauth-buttons';

    import s from './header.module.css';

    import { page } from '$app/stores';

    export let isDesktop: boolean;

    $: user = $userStore.user;
    $: isNotOperator =
        Boolean(user && !$isOperator && user.gender && user.isEmailVerified) &&
        user?.identityStatus !== IdentityStatus.Pending;
    $: isVerified = user?.isEmailVerified && user?.identityStatus === IdentityStatus.Verified;
    $: isLandingPage = landingPages().includes($page.url.pathname);
    $: isMenuShow = user
        && $page.url.pathname !== URLS.roomCall()
        && $page.url.pathname !== URLS.roomSearch()
        && (!$page.route?.id || !URLS.isRoom($page.route.id))
        && (!$page.route?.id || !URLS.isMessage($page.route.id) || isDesktop)
        && ($page.url.pathname !== URLS.roomSettings() || isDesktop);
    $: canBuyMinutes = paymentStore.canBuyMinutes($paymentStore);
    let isSale = configService.isFeatureAllowed(Feature.Sale);
</script>

<header class={cn(s.root, { [s.desktop]: isDesktop, [s.fixed]: isLandingPage })}>
    <div class={cn(isLandingPage ? sc.mainContainer : s.container)}>
        <Logo link={user ? URLS.roomStart() : URLS.index()}/>

        {#if isMenuShow}
            <Menu/>
        {/if}

        {#if !configService.get('isUnsupportedDevice')}
            <nav class={cn(s.buttonsWrapper, { [s.authorized]: user })}>

                {#if isNotOperator && user?.status !== UserStatus.Blocked}
                    {#if !canBuyMinutes
                        || $totalSeconds !== null && $totalSeconds <= 0
                        || isSale && !$isMinutesRunningOut
                    }
                        <Button
                            attrs={{ 'data-testid': canBuyMinutes ? 'buyMinutes' : 'buyMembership' }}
                            classes={cn(s.buyMembershipButton, {
                                [s.tiny]: !isDesktop,
                            })}
                            color={ButtonTypes.Color.Brand}
                            size={Size.Small}
                            on:click={() => paymentStore.showBuyDialog(
                                isSale && (isDesktop || canBuyMinutes && isNumber($totalSeconds) && $totalSeconds > 0)
                                    ? PaymentSource.Sale
                                    : (
                                        canBuyMinutes
                                            ? PaymentSource.ButtonBuyMinutes
                                            : PaymentSource.ButtonBuyMembership
                                    )
                            )}
                        >
                            {canBuyMinutes ? $LL.auth.buyMinutes() : $LL.auth.buyMembership()}
                            {#if isDesktop && isSale}
                                &nbsp;<Sale value={
                                    canBuyMinutes
                                    || !configService.isPremiumAllowed(user)
                                        ? SALE_SUBSCRIPTION_PERCENT
                                        : SALE_MINUTES_PERCENT
                                    }
                                       type={SalePropType.Round}
                            />
                            {/if}
                        </Button>
                    {:else if $isMinutesRunningOut}
                        <Button
                            attrs={{ 'data-testid': 'totalMinutes' }}
                            classes={s.userBalance}
                            size={Size.Small}
                            on:click={() => dialogStore.showBuyMinutes({ source: PaymentSource.ButtonBuyMinutes })}
                        >
                            {$totalMinutes}
                            {$LL.profile.subscription.balance.min()}
                        </Button>
                    {/if}
                {/if}

                <SettingsDropdown/>
                {#if $isOperator && isVerified}
                    <Balance {isDesktop} />
                {/if}

                {#if !user}
                    <UnauthButtons />
                {:else}
                    {#await import('@components/layout/header/components/profile-dropdown') then { ProfileDropdown } }
                        <ProfileDropdown {isMenuShow}/>
                    {/await}
                {/if}
            </nav>
        {/if}
    </div>
</header>
